import { useDispatch } from '@redux';
import { editOrdersPickingList } from '@redux/ordersPickingList/actions';
import { Input, InputNumber } from 'antd';
import { TFilterItem } from 'components/rest/Filter/FilterRow';
import { IRestColumnProps } from 'components/rest/Table/TableColumn';
import {
  CHANNELS_FILTER,
  DELIVERY_PARTNER_FILTER,
  IS_VOUCHER,
  ORDER_TYPE,
  PAYMENT_STATUSES,
  SHIPPING_METHOD,
} from 'constants/constants';
import FilterComponent from 'containers/Orders/components/Filters/FilterComponent';
import RestList from 'containers/rest/List';
import { debounce, upperFirst } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { DynamicObjectType } from 'types/commons';
import { getDataDetail } from 'utils/tools';

const PickingList = ({ currentData }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const handleEditRow = (dt: DynamicObjectType) => {
    dispatch(
      editOrdersPickingList({
        data: {
          id: dt.id,
          ...dt,
        },
        options: {
          customApiResource: `order-batch-items`,
        },
      }),
    );
  };

  const onEditDebounce = debounce((value) => {
    handleEditRow(value);
  }, 300);

  const columns: IRestColumnProps[] = [
    {
      name: 'variantSku',
      title: 'SKU',
      isDefault: true,
    },
    {
      name: 'variantName',
      title: 'SKU Name',
      isDefault: true,
      render: (d: string, rc: DynamicObjectType) => {
        return (
          <>
            <div>{d || ''}</div>
            <div className="text-sm text-secondary">
              {rc?.attributeValues
                ?.map((item) => getDataDetail(item)?.name)
                ?.join('/')}
            </div>
          </>
        );
      },
    },
    {
      name: 'quantity',
      title: 'Quantity',
      isDefault: true,
    },
    {
      name: 'pickedQuantity',
      title: 'Picked Quantity',
      isDefault: true,
      render: (d: number, r, i, handleEditRow) => (
        <InputNumber
          value={d}
          onChange={(value) =>
            handleEditRow(
              { id: r.id, pickedQuantity: value },
              { customApiResource: `order-batch-items` },
            )
          }
          className="w-[100px] max-w-[100px]"
          disabled
        />
      ),
    },
    {
      name: 'isPicked',
      title: 'Status',
      render: (d: boolean) => (
        <div
          className={`rounded-[50%] w-3 h-3 ${d ? 'bg-[#88C041]' : 'bg-[#FFA940]'}`}
        />
      ),
      isDefault: true,
    },
    {
      name: 'note',
      title: 'Note',
      isDefault: true,
      render: (d: string, r) => (
        <Input
          onChange={(e) => {
            onEditDebounce({
              id: r.id,
              note: e.currentTarget.value,
            });
          }}
          defaultValue={d}
        />
      ),
    },
  ];

  const filters: TFilterItem[] = [
    // {
    //   key: 'orders.statusOms',
    //   label: 'Order Status',
    //   dataSource: toArray(SELLER_ORDER_STATUS_CONST).map(
    //     (e: DynamicObjectType) => ({
    //       value: e.value,
    //       label: t(e.text),
    //     }),
    //   ),
    // },
    {
      key: 'voucher',
      label: 'Order with Voucher',
      dataSource: IS_VOUCHER,
    },
    {
      key: 'orders.paymentStatus',
      label: 'Payment Status',
      dataSource: PAYMENT_STATUSES.map((e: DynamicObjectType) => ({
        value: e.value,
        label: t(e.text),
      })),
    },
    {
      key: 'orders.paymentType',
      label: 'Payment Method',
      valueCPN: (
        <FilterComponent
          reference={{
            model: 'payment-method',
            valueProp: 'name',
            labelFormat: (e) => upperFirst(e?.name || ''),
            initFilters: {
              filters: [
                {
                  condition: 'and',
                  key: 'isActive',
                  operator: 'eq',
                  value: true,
                },
              ],
            },
          }}
        />
      ),
    },
    {
      key: 'createdAt',
      label: 'Order created time',
      isDatePicker: true,
    },
    // {
    //   key: 'cancelTime',
    //   label: 'Order cancelled time',
    //   isDatePicker: true,
    // },
    // {
    //   key: 'postInvoiceDate',
    //   label: 'Invoice creation time',
    //   isDatePicker: true,
    // },
    {
      key: 'orders.orderType',
      label: 'Order Type',
      dataSource: ORDER_TYPE.map((e: DynamicObjectType) => ({
        value: e.value,
        label: t(e.text),
      })),
    },
    {
      key: 'channel',
      label: 'Channel',
      dataSource: CHANNELS_FILTER.map((e: DynamicObjectType) => ({
        value: e.value,
        label: t(e.text),
      })),
    },
    {
      key: 'orders.shippingType',
      label: 'Delivery type',
      dataSource: SHIPPING_METHOD.map((e: DynamicObjectType) => ({
        value: e.key,
        label: t(e.text),
      })),
    },
    {
      key: 'orders.shippingCarrier.name',
      label: 'Delivery partner',
      dataSource: DELIVERY_PARTNER_FILTER,
    },
    {
      key: 'isGiftCards',
      label: 'Gift Cards',
      dataSource: IS_VOUCHER,
    },
    // {
    //   key: 'sipNumber',
    //   label: 'SIP number',
    //   dataSource: IS_VOUCHER,
    // },
    {
      key: 'orders.locationId',
      label: 'Warehouse',
      valueCPN: (
        <FilterComponent
          reference={{
            labelProp: 'name',
            model: 'locations',
            searchMoreKeys: ['id', 'name'],
            valueProp: 'id',
            labelFormat: (item) =>
              item?.name ? `${item?.name} | ${item?.code}` : item?.code,
            initFilters: {
              filters: [
                {
                  condition: 'and',
                  key: 'isOrderProcess',
                  operator: 'eq',
                  value: true,
                },
              ],
            },
          }}
        />
      ),
    },
    {
      key: 'note',
      label: 'Note',
      isCheckNull: true,
      dataSource: [
        {
          value: true,
          label: 'True',
        },
        {
          value: false,
          label: 'False',
        },
      ],
    },
  ];

  return (
    <RestList
      columns={columns}
      model="ordersPickingList"
      filters={filters}
      searchKeys={['sku']}
      hasOnlyFilter
      placeholderInputSearch="Search by SKU, SKU Name..."
      hasImport={false}
      hasExport={false}
      hasCustomLayout
      defaultOptions={{
        customApiResource: `order-batch-items/${currentData?.id}/view-items`,
      }}
    />
  );
};

export default PickingList;
